import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import CheckIcon from '@mui/icons-material/Check'
import AddLangIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'

import { adminApi } from '../../api/endpoints'
import { useLocale, SUPPORTED_LOCALES } from '../../i18n/LocaleProvider'
import { ErrorState } from '../../components/common/States'
import { seasonColors } from '../../theme/theme'
import { FlagIcon } from '../../components/common/FlagIcon'
import type {
  ActivityTranslationDto,
  Season,
  SeasonalActivityDto,
  SeasonalActivityRequestPayload,
} from '../../types/api'

const SEASONS: Season[] = ['WINTER', 'SPRING', 'SUMMER', 'AUTUMN', 'ALL_YEAR']

const EMOJI_CHOICES = [
  '🎣', '🧖', '🥾', '🌌', '🦢', '🐟', '🎭', '🌅', '🫐', '🚣',
  '🍄', '🍂', '🔥', '⛷️', '🛷', '🚴', '🛶', '📸', '🐕', '🍽️',
]

/**
 * The screen the owner opens most often.
 *
 * Two design decisions are worth stating. First, the emoji is chosen from a palette rather
 * than typed: it is the visual identity of the card on the public site, and asking somebody
 * to paste a character from their keyboard would produce inconsistent results. Second, the
 * translation tabs are part of the same form, not a separate workflow, so adding the German
 * text is one dialog rather than a second trip through a list.
 */
export function AdminActivitiesPage() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<SeasonalActivityDto | 'new' | null>(null)
  // Which language the editor opens on. The badges set it, so clicking "RU" lands on the
  // Russian text rather than on the English form with Russian two clicks away.
  const [editingLocale, setEditingLocale] = useState('en')
  const openEditor = (target: SeasonalActivityDto | 'new', code = 'en') => {
    setEditingLocale(code)
    setEditing(target)
  }

  const activities = useQuery({
    queryKey: ['admin', 'activities', locale],
    queryFn: () => adminApi.activities(locale),
  })

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteActivity(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'activities'] }),
  })

  const grouped = SEASONS.map((season) => ({
    season,
    items: (activities.data ?? []).filter((activity) => activity.season === season),
  })).filter((group) => group.items.length > 0)

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h2">{t('admin.activities')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEditor('new')}>
          {t('admin.addActivity')}
        </Button>
      </Stack>

      {activities.isError && (
        <ErrorState error={activities.error} onRetry={() => void activities.refetch()} />
      )}

      <Stack spacing={4}>
        {grouped.map((group) => (
          <Box key={group.season}>
            <Chip
              label={t(`activities.${group.season}`)}
              sx={{
                mb: 2,
                bgcolor: seasonColors[group.season],
                color: 'common.white',
                fontWeight: 700,
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
              }}
            >
              {group.items.map((activity) => (
                <Paper
                  key={activity.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 3,
                    opacity: activity.active ? 1 : 0.55,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box aria-hidden sx={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                      {activity.icon ?? '📍'}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {activity.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {activity.summary}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        {!activity.active && <Chip size="small" label="Hidden" />}
                        {activity.featured && <Chip size="small" color="secondary" label="★" />}
                        <TranslationBadges
                          translations={activity.translations}
                          onPick={(code) => openEditor(activity, code)}
                        />
                      </Stack>
                    </Box>
                    <Stack>
                      <IconButton size="small" onClick={() => openEditor(activity)} aria-label="edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="delete"
                        onClick={() => {
                          if (window.confirm(t('admin.deleteConfirm'))) remove.mutate(activity.id)
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>

      {editing && (
        <ActivityDialog
          activity={editing === 'new' ? null : editing}
          initialLocale={editingLocale}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['admin', 'activities'] })
            setEditing(null)
          }}
        />
      )}
    </Box>
  )
}

/**
 * Which languages an activity has, and the way into each one.
 *
 * These used to be display-only: green for "translated", grey for "falls back to English", and
 * clicking them did nothing - so the owner reasonably concluded that languages could not be
 * changed at all. Each badge now opens the editor on that language, with a tick where the
 * text exists and a plus where it is still missing.
 */
function TranslationBadges({
  translations,
  onPick,
}: {
  translations?: Record<string, ActivityTranslationDto> | null
  onPick: (code: string) => void
}) {
  return (
    <>
      {SUPPORTED_LOCALES.filter((option) => option.code !== 'en').map((option) => {
        const present = Boolean(translations?.[option.code.toUpperCase()]?.title)
        return (
          <Tooltip key={option.code} title={option.nativeName}>
            <Chip
              size="small"
              clickable
              onClick={() => onPick(option.code)}
              variant={present ? 'filled' : 'outlined'}
              color={present ? 'success' : 'default'}
              icon={present ? <CheckIcon /> : <AddLangIcon />}
              label={option.code.toUpperCase()}
              aria-label={`${option.nativeName}: ${present ? 'edit' : 'add'}`}
              sx={{ height: 22, fontSize: '0.68rem', '& .MuiChip-icon': { fontSize: 13 } }}
            />
          </Tooltip>
        )
      })}
    </>
  )
}

function ActivityDialog({
  activity,
  initialLocale = 'en',
  onClose,
  onSaved,
}: {
  activity: SeasonalActivityDto | null
  /** 'en' opens the main form; any other code opens that language's tab. */
  initialLocale?: string
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const otherLocales = SUPPORTED_LOCALES.filter((option) => option.code !== 'en')
  // Tab 0 is English; tab n is otherLocales[n - 1].
  const [tab, setTab] = useState(() => {
    const index = otherLocales.findIndex((option) => option.code === initialLocale)
    return index >= 0 ? index + 1 : 0
  })

  const [form, setForm] = useState<SeasonalActivityRequestPayload>(() => ({
    slug: activity?.slug ?? '',
    title: activity?.title ?? '',
    summary: activity?.summary ?? '',
    description: activity?.description ?? '',
    season: activity?.season ?? 'SUMMER',
    icon: activity?.icon ?? '📍',
    imageUrl: activity?.imageUrl ?? '',
    difficulty: activity?.difficulty ?? '',
    durationMinutes: activity?.durationMinutes ?? null,
    distanceKm: activity?.distanceKm ?? '',
    priceInfo: activity?.priceInfo ?? '',
    bookingRequired: activity?.bookingRequired ?? false,
    providerName: activity?.providerName ?? '',
    providerUrl: activity?.providerUrl ?? '',
    featured: activity?.featured ?? false,
    displayOrder: activity?.displayOrder ?? 0,
    active: activity?.active ?? true,
    translations: activity?.translations ?? {},
  }))

  const save = useMutation({
    mutationFn: () =>
      activity ? adminApi.updateActivity(activity.id, form) : adminApi.createActivity(form),
    onSuccess: onSaved,
  })

  const set = <K extends keyof SeasonalActivityRequestPayload>(
    key: K,
    value: SeasonalActivityRequestPayload[K],
  ) => setForm((previous) => ({ ...previous, [key]: value }))

  const setTranslation = (code: string, patch: Partial<ActivityTranslationDto>) =>
    setForm((previous) => ({
      ...previous,
      translations: {
        ...previous.translations,
        [code]: { ...previous.translations?.[code], ...patch },
      },
    }))

  // Slug is derived from the English title on create, and never touched again afterwards:
  // it is in published URLs, and changing it silently breaks every link the owner shared.
  const deriveSlug = (title: string) =>
    title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 140)

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{activity ? t('admin.editActivity') : t('admin.addActivity')}</DialogTitle>

      <DialogContent dividers>
        {/*
          One tab per language, not one long "Translations" tab. A language you can see named
          at the top is a language you know you can edit; six stacked forms behind a second tab
          is how the owner came to believe it could not be done. The tick says which are done.
        */}
        <Tabs
          value={tab}
          onChange={(_, value: number) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2.5 }}
        >
          <Tab label="English" />
          {otherLocales.map((option) => {
            const done = Boolean(form.translations?.[option.code.toUpperCase()]?.title)
            return (
              <Tab
                key={option.code}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <FlagIcon locale={option.code} size={16} title={option.englishName} />
                    <span>{option.code.toUpperCase()}</span>
                    {done && <CheckIcon sx={{ fontSize: 15, color: 'success.main' }} />}
                  </Stack>
                }
              />
            )
          })}
        </Tabs>

        {tab === 0 && (
          <Stack spacing={2}>
            <TextField
              required
              label="Title"
              value={form.title}
              onChange={(event) => {
                set('title', event.target.value)
                if (!activity) set('slug', deriveSlug(event.target.value))
              }}
            />
            <TextField
              label="Slug"
              value={form.slug}
              onChange={(event) => set('slug', event.target.value)}
              disabled={Boolean(activity)}
              helperText={activity ? 'Slugs are permanent: they are in published URLs.' : undefined}
            />
            <TextField
              label="Summary"
              value={form.summary ?? ''}
              onChange={(event) => set('summary', event.target.value)}
              multiline
              minRows={2}
            />
            <TextField
              label="Description"
              value={form.description ?? ''}
              onChange={(event) => set('description', event.target.value)}
              multiline
              minRows={4}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                fullWidth
                label={t('explore.filterSeason')}
                value={form.season}
                onChange={(event) => set('season', event.target.value as Season)}
              >
                {SEASONS.map((season) => (
                  <MenuItem key={season} value={season}>
                    {t(`activities.${season}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                type="number"
                label="Duration (minutes)"
                value={form.durationMinutes ?? ''}
                onChange={(event) =>
                  set('durationMinutes', event.target.value ? Number(event.target.value) : null)
                }
              />
            </Stack>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Icon
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                {EMOJI_CHOICES.map((emoji) => (
                  <Box
                    key={emoji}
                    component="button"
                    type="button"
                    onClick={() => set('icon', emoji)}
                    aria-label={emoji}
                    aria-pressed={form.icon === emoji}
                    sx={{
                      appearance: 'none',
                      cursor: 'pointer',
                      fontSize: '1.3rem',
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      border: 2,
                      borderColor: form.icon === emoji ? 'primary.main' : 'transparent',
                      bgcolor: form.icon === emoji ? 'action.selected' : 'transparent',
                    }}
                  >
                    {emoji}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Difficulty"
                value={form.difficulty ?? ''}
                onChange={(event) => set('difficulty', event.target.value)}
              />
              <TextField
                fullWidth
                label="Distance"
                value={form.distanceKm ?? ''}
                onChange={(event) => set('distanceKm', event.target.value)}
              />
            </Stack>

            <TextField
              label="Price information"
              value={form.priceInfo ?? ''}
              onChange={(event) => set('priceInfo', event.target.value)}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Provider"
                value={form.providerName ?? ''}
                onChange={(event) => set('providerName', event.target.value)}
              />
              <TextField
                fullWidth
                label="Provider URL"
                value={form.providerUrl ?? ''}
                onChange={(event) => set('providerUrl', event.target.value)}
              />
            </Stack>

            <Divider />

            <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.bookingRequired}
                    onChange={(event) => set('bookingRequired', event.target.checked)}
                  />
                }
                label={t('activities.bookingRequired')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.featured}
                    onChange={(event) => set('featured', event.target.checked)}
                  />
                }
                label="Featured"
              />
              <FormControlLabel
                control={
                  <Switch checked={form.active} onChange={(event) => set('active', event.target.checked)} />
                }
                label="Visible"
              />
            </Stack>
          </Stack>
        )}

        {tab > 0 &&
          (() => {
            const option = otherLocales[tab - 1]
            const key = option.code.toUpperCase()
            const value = form.translations?.[key] ?? {}
            return (
              <Paper
                key={option.code}
                elevation={0}
                variant="outlined"
                sx={{ p: 2, borderRadius: 3 }}
                dir={option.dir}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} dir="ltr">
                  <FlagIcon locale={option.code} size={20} title={option.englishName} />
                  <Typography variant="subtitle2">{option.nativeName}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} dir="ltr">
                  {t('admin.translationMissing')}
                </Typography>
                {/* The English text alongside, so a translation is written from the source and
                    not from memory of it. */}
                <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }} dir="ltr">
                  <Typography variant="caption" color="text.secondary" display="block">
                    English
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {form.title || '-'}
                  </Typography>
                  {form.summary && (
                    <Typography variant="body2" color="text.secondary">
                      {form.summary}
                    </Typography>
                  )}
                </Paper>
                <Stack spacing={1.5}>
                  <TextField
                    autoFocus
                    size="small"
                    label="Title"
                    value={value.title ?? ''}
                    onChange={(event) => setTranslation(key, { title: event.target.value })}
                  />
                  <TextField
                    size="small"
                    label="Summary"
                    multiline
                    minRows={2}
                    value={value.summary ?? ''}
                    onChange={(event) => setTranslation(key, { summary: event.target.value })}
                  />
                  <TextField
                    size="small"
                    label="Description"
                    multiline
                    minRows={3}
                    value={value.description ?? ''}
                    onChange={(event) => setTranslation(key, { description: event.target.value })}
                  />
                </Stack>
              </Paper>
            )
          })()}

        {save.isError && <ErrorState error={save.error} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
